import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const LicensePlateImage = ({ countryCode, imageNumber, difficulty, showOriginal }) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [imageSrc, setImageSrc] = useState('');
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    const imageType = (difficulty === 'hard' && !showOriginal) ? 'blurred' : 'original';
    
    // Déterminer la qualité basée sur la difficulté et le type
    let quality = '80'; // Qualité par défaut
    let width = null; // Largeur par défaut (pas de redimensionnement)
    
    // Pour les images floues, on peut utiliser une qualité moindre
    if (imageType === 'blurred') {
      quality = '70';
      width = '600'; // Redimensionner à 600px de large max
    } else {
      quality = '85';
      width = '800'; // Images originales à 800px max
    }
    
    // Construire l'URL avec l'API de compression
    const compressedImageSrc = `/api/image/plates/${countryCode}/${imageType}/${imageNumber}.png?quality=${quality}&width=${width}`;
    
    // URL de fallback (image originale)
    const fallbackImageSrc = `images/license-plates/${countryCode}/${imageType}/${imageNumber}.png?t=${new Date().getTime()}`;
    
    setIsLoading(true);
    setImageError(false);
    
    const img = new Image();
    
    img.onload = () => {
      setImageSrc(compressedImageSrc);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      // Si l'image compressée échoue, essayer l'image originale
      const fallbackImg = new Image();
      
      fallbackImg.onload = () => {
        setImageSrc(fallbackImageSrc);
        setIsLoading(false);
      };
      
      fallbackImg.onerror = () => {
        setImageError(true);
        setIsLoading(false);
      };
      
      fallbackImg.src = fallbackImageSrc;
    };
    
    // Commencer par essayer l'image compressée
    img.src = compressedImageSrc;
    
  }, [countryCode, imageNumber, difficulty, showOriginal]);

  // Intersection Observer pour le lazy loading
  useEffect(() => {
    if (!imageRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // L'image est visible, on peut maintenant charger l'image haute résolution si nécessaire
            entry.target.classList.add('loaded');
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(imageRef.current);

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  return (
    <div className="image-container" ref={imageRef}>
      {isLoading && <div className="loading"></div>}
      {!imageError ? (
        <img
          src={imageSrc}
          alt="License Plate"
          className="license-plate-image"
          style={{ 
            opacity: isLoading ? 0.5 : 1,
            transition: 'opacity 0.3s ease'
          }}
          loading="lazy" // Lazy loading natif du navigateur
        />
      ) : (
        <div className="image-error">{t('imageNotFound')}</div>
      )}
    </div>
  );
};

export default LicensePlateImage;